import { useCallback, useLayoutEffect, useRef } from 'react';

const resolveViewportSize = () => {
  if (typeof window !== 'undefined') {
    return {
      width: Number(window.innerWidth) || 0,
      height: Number(window.innerHeight) || 0,
    };
  }
  if (typeof document !== 'undefined') {
    const element = document.documentElement;
    return {
      width: Number(element?.clientWidth) || 0,
      height: Number(element?.clientHeight) || 0,
    };
  }
  return { width: 0, height: 0 };
};

export const clampViewportBoundedMenuPosition = ({
  x = 0,
  y = 0,
  width = 0,
  height = 0,
  padding = 12,
  fallbackWidth = 248,
  fallbackHeight = 320,
} = {}) => {
  const viewport = resolveViewportSize();
  const safePadding = Math.max(0, Number(padding) || 0);
  const menuWidth = Math.max(0, Number(width) || 0, Number(fallbackWidth) || 0);
  const menuHeight = Math.max(0, Number(height) || 0, Number(fallbackHeight) || 0);
  const maxX = Math.max(safePadding, viewport.width - safePadding - menuWidth);
  const maxY = Math.max(safePadding, viewport.height - safePadding - menuHeight);

  return {
    x: Math.max(safePadding, Math.min(Number(x) || 0, maxX)),
    y: Math.max(safePadding, Math.min(Number(y) || 0, maxY)),
  };
};

export default function useViewportBoundedMenuPosition({
  menu = null,
  setMenu,
  padding = 12,
  fallbackWidth = 248,
  fallbackHeight = 320,
} = {}) {
  const menuRef = useRef(null);

  const resolveMenuPosition = useCallback(
    (x, y, size = {}) =>
      clampViewportBoundedMenuPosition({
        x,
        y,
        width: size?.width,
        height: size?.height,
        padding,
        fallbackWidth,
        fallbackHeight,
      }),
    [fallbackHeight, fallbackWidth, padding]
  );

  useLayoutEffect(() => {
    if (!menu || typeof setMenu !== 'function') return undefined;

    const updatePosition = () => {
      const rect = menuRef.current?.getBoundingClientRect?.();
      const next = resolveMenuPosition(menu?.x, menu?.y, {
        width: rect?.width,
        height: rect?.height,
      });

      setMenu((current) => {
        if (!current) return current;
        if (Math.abs(Number(current.x) - next.x) < 1 && Math.abs(Number(current.y) - next.y) < 1) {
          return current;
        }
        return { ...current, x: next.x, y: next.y };
      });
    };

    updatePosition();

    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined' && menuRef.current) {
      resizeObserver = new ResizeObserver(() => {
        updatePosition();
      });
      resizeObserver.observe(menuRef.current);
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updatePosition);
    }

    return () => {
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', updatePosition);
      }
      resizeObserver?.disconnect?.();
    };
  }, [menu, resolveMenuPosition, setMenu]);

  return { menuRef, resolveMenuPosition };
}
